import { FC } from "react";

import { Stack } from "@mui/material";
import { TicketingFormCategory } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../../../Context/RateContext";
import { useRateFieldName } from "../../../../hooks/useRateFieldName";
import { RateOptionToggleRow } from "../../RateOptionToggleRow";

import { EndsOnFields } from "./EndsOnFields";
import { StartsOnFields } from "./StartsOnFields";
import { useScheduleWindowValidation } from "./useScheduleWindowValidation";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Date as DateIcon } from "@/icons/outlined";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { DashboardTranslationKey } from "@/public/@types/translations";

const SCHEDULE_LABEL_KEYS: Record<TicketingFormCategory, DashboardTranslationKey> = {
  [TicketingFormCategory.Event]: "ticketing.rateOption.schedule",
  [TicketingFormCategory.Lottery]: "ticketing.rateOption.schedule",
  [TicketingFormCategory.Auction]: "ticketing.rateOption.schedule",
  [TicketingFormCategory.Sweepstakes]: "sweepstakes.rates.schedule",
  [TicketingFormCategory.Shop]: "ticketing.rateOption.schedule.item",
  [TicketingFormCategory.Custom]: "ticketing.rateOption.schedule.item",
  [TicketingFormCategory.MembershipV2]: "ticketing.rateOption.schedule.membership",
};

interface ScheduleRowProps {
  isSweepstakes?: boolean;
}

export const ScheduleRow: FC<ScheduleRowProps> = ({ isSweepstakes = false }) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { formCategory, isEditing, isOnboarding } = useRateContext();
  const { control, setValue } = useFormContext<StepTicketsFormData>();

  const isScheduledField = useRateFieldName("isScheduled");
  const startModeField = useRateFieldName("salesStartMode");
  const startDateField = useRateFieldName("salesStartDate");
  const startAfterRateField = useRateFieldName("salesStartAfterRateId");
  const endModeField = useRateFieldName("salesEndMode");
  const endDateField = useRateFieldName("salesEndDate");

  const scheduleLabel = t("dashboard", SCHEDULE_LABEL_KEYS[formCategory]);

  const isChecked = useWatch({ control, name: isScheduledField });
  useScheduleWindowValidation();
  const startMode = useWatch({ control, name: startModeField });
  const endMode = useWatch({ control, name: endModeField });

  const handleToggle = (checked: boolean) => {
    setValue(isScheduledField, checked, { shouldDirty: true });
    setValue(startModeField, "formDefault");
    setValue(startDateField, null);
    setValue(startAfterRateField, null);
    setValue(endModeField, "formDefault");
    setValue(endDateField, null);
    logAmplitudeEvent(AmplitudeEvents.TicketOptionToggled, {
      option: "schedule",
      enabled: checked,
      start_mode: checked ? "formDefault" : startMode,
      end_mode: checked ? "formDefault" : endMode,
      entry_point: "manual",
      form_type: formCategory,
      is_liveform: true,
      isEditing,
      isOnboarding,
    });
  };

  return (
    <RateOptionToggleRow
      optionKey="schedule"
      checked={Boolean(isChecked)}
      onToggle={handleToggle}
      icon={<DateIcon />}
      label={
        <>
          {scheduleLabel}{" "}
          <DashboardTypography component="span" variant="caption" vibe="text-neutral-quiet">
            {t("dashboard", "ticketing.scheduleTicketHint")}
          </DashboardTypography>
        </>
      }
      ariaLabel={scheduleLabel}
      belowContent={
        isChecked ? (
          <Stack spacing={2} sx={{ marginBottom: 1, marginTop: 1.5, paddingLeft: 4.5 }}>
            <StartsOnFields isSweepstakes={isSweepstakes} />
            <EndsOnFields isSweepstakes={isSweepstakes} />
          </Stack>
        ) : undefined
      }
      data-test="rate-option-switch-schedule"
    />
  );
};
